import {
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { QueryFetchFunction, QueryTransformer } from 'common/helpers/common';
import {
  ApiError,
  ApiResult,
  ExtendedApiResult,
} from 'common/repositories/common.model';

import {
  getReportsInput,
  getReportTourDetailShowInput,
  getReportTourDetailsInput,
  ReportingResultModel,
  ReportTourDetailCommissionModel,
  ReportTourDetailOtherExpenseModel,
  ReportTourDetailOtherIncomeModel,
  ReportTourDetailPurchaseModel,
  ReportTourDetailSaleModel,
  ReportTourDetailShowModel,
} from './model';

export function useGetAccountPayableReports(
  input?: getReportsInput,
  options?: UseQueryOptions<ReportingResultModel, ApiError>,
): UseQueryResult<ReportingResultModel, ApiError> {
  return QueryTransformer(
    useQuery(
      ['getAccountPayableReports', input?.params],
      () =>
        QueryFetchFunction({
          url: `reports/account-payable`,
          params: input?.params,
        }),
      options,
    ),
    ReportingResultModel,
  );
}
export function useGetAccountReceivableReports(
  input?: getReportsInput,
  options?: UseQueryOptions<ReportingResultModel, ApiError>,
): UseQueryResult<ReportingResultModel, ApiError> {
  return QueryTransformer(
    useQuery(
      ['getAccountReceivableReports', input?.params],
      () =>
        QueryFetchFunction({
          url: `reports/account-receivable`,
          params: input?.params,
        }),
      options,
    ),
    ReportingResultModel,
  );
}

export function useGetProjectListReports(
  input?: getReportsInput,
  options?: UseQueryOptions<ReportingResultModel, ApiError>,
): UseQueryResult<ReportingResultModel, ApiError> {
  return QueryTransformer(
    useQuery(
      ['getProjectListReports', input?.params],
      () =>
        QueryFetchFunction({
          url: `reports/project`,
          params: input?.params,
        }),
      options,
    ),
    ReportingResultModel,
  );
}

export function useGetTransactionReports(
  input?: getReportsInput,
  options?: UseQueryOptions<ReportingResultModel, ApiError>,
): UseQueryResult<ReportingResultModel, ApiError> {
  return QueryTransformer(
    useQuery(
      ['getTransactionReports', input?.params],
      () =>
        QueryFetchFunction({
          url: `reports/transaction-mutation`,
          params: input?.params,
        }),
      options,
    ),
    ReportingResultModel,
  );
}

export function useGetBankMutationReports(
  input?: getReportsInput,
  options?: UseQueryOptions<ReportingResultModel, ApiError>,
): UseQueryResult<ReportingResultModel, ApiError> {
  return QueryTransformer(
    useQuery(
      ['getBankMutationReports', input?.params],
      () =>
        QueryFetchFunction({
          url: `reports/bank-mutation`,
          params: input?.params,
        }),
      options,
    ),
    ReportingResultModel,
  );
}

export function useGetCashMutationReports(
  input?: getReportsInput,
  options?: UseQueryOptions<ReportingResultModel, ApiError>,
): UseQueryResult<ReportingResultModel, ApiError> {
  return QueryTransformer(
    useQuery(
      ['getCashMutationReports', input?.params],
      () =>
        QueryFetchFunction({
          url: `reports/cash-mutation`,
          params: input?.params,
        }),
      options,
    ),
    ReportingResultModel,
  );
}

export function useGetAgentMutationReports(
  input?: getReportsInput,
  options?: UseQueryOptions<ReportingResultModel, ApiError>,
): UseQueryResult<ReportingResultModel, ApiError> {
  return QueryTransformer(
    useQuery(
      ['getAgentMutationReports', input?.params],
      () =>
        QueryFetchFunction({
          url: `reports/agent-mutation`,
          params: input?.params,
        }),
      options,
    ),
    ReportingResultModel,
  );
}

export function useGetCustomerMutationReports(
  input?: getReportsInput,
  options?: UseQueryOptions<ReportingResultModel, ApiError>,
): UseQueryResult<ReportingResultModel, ApiError> {
  return QueryTransformer(
    useQuery(
      ['getCustomerMutationReports', input?.params],
      () =>
        QueryFetchFunction({
          url: `reports/customer-mutation`,
          params: input?.params,
        }),
      options,
    ),
    ReportingResultModel,
  );
}

export function useGetFlightReports(
  input?: getReportsInput,
  options?: UseQueryOptions<ReportingResultModel, ApiError>,
): UseQueryResult<ReportingResultModel, ApiError> {
  return QueryTransformer(
    useQuery(
      ['getFlightReports', input?.params],
      () =>
        QueryFetchFunction({
          url: `reports/flight`,
          params: input?.params,
        }),
      options,
    ),
    ReportingResultModel,
  );
}

export function useGetHotelReports(
  input?: getReportsInput,
  options?: UseQueryOptions<ReportingResultModel, ApiError>,
): UseQueryResult<ReportingResultModel, ApiError> {
  return QueryTransformer(
    useQuery(
      ['getHotelReports', input?.params],
      () =>
        QueryFetchFunction({
          url: `reports/hotel`,
          params: input?.params,
        }),
      options,
    ),
    ReportingResultModel,
  );
}

export function useGetPurchaseReports(
  input?: getReportsInput,
  options?: UseQueryOptions<ReportingResultModel, ApiError>,
): UseQueryResult<ReportingResultModel, ApiError> {
  return QueryTransformer(
    useQuery(
      ['getPurchaseReports', input?.params],
      () =>
        QueryFetchFunction({
          url: `reports/purchase`,
          params: input?.params,
        }),
      options,
    ),
    ReportingResultModel,
  );
}

export function useGetSaleReports(
  input?: getReportsInput,
  options?: UseQueryOptions<ReportingResultModel, ApiError>,
): UseQueryResult<ReportingResultModel, ApiError> {
  return QueryTransformer(
    useQuery(
      ['getSaleReports', input?.params],
      () =>
        QueryFetchFunction({
          url: `reports/sale`,
          params: input?.params,
        }),
      options,
    ),
    ReportingResultModel,
  );
}

export function useGetIncomeStatementReports(
  input?: getReportsInput,
  options?: UseQueryOptions<ReportingResultModel, ApiError>,
): UseQueryResult<ReportingResultModel, ApiError> {
  return QueryTransformer(
    useQuery(
      ['getProfitLossMutationReports', input?.params],
      () =>
        QueryFetchFunction({
          url: `reports/profit-loss-mutation`,
          params: input?.params,
        }),
      options,
    ),
    ReportingResultModel,
  );
}

export function useGetAccountReceivableRegularReports(
  input?: getReportsInput,
  options?: UseQueryOptions<ReportingResultModel, ApiError>,
): UseQueryResult<ReportingResultModel, ApiError> {
  return QueryTransformer(
    useQuery(
      ['getAccountReceivableRegularReports', input?.params],
      () =>
        QueryFetchFunction({
          url: `reports/account-receivable-regular`,
          params: input?.params,
        }),
      options,
    ),
    ReportingResultModel,
  );
}

export function useGetAccountPayableRegularReports(
  input?: getReportsInput,
  options?: UseQueryOptions<ReportingResultModel, ApiError>,
): UseQueryResult<ReportingResultModel, ApiError> {
  return QueryTransformer(
    useQuery(
      ['getAccountPayableRegularReports', input?.params],
      () =>
        QueryFetchFunction({
          url: `reports/account-payable-regular`,
          params: input?.params,
        }),
      options,
    ),
    ReportingResultModel,
  );
}

export function useGetTourReports(
  input?: getReportsInput,
  options?: UseQueryOptions<ReportingResultModel, ApiError>,
): UseQueryResult<ReportingResultModel, ApiError> {
  return QueryTransformer(
    useQuery(
      ['getTourReports', input?.params],
      () =>
        QueryFetchFunction({
          url: `reports/tour`,
          params: input?.params,
        }),
      options,
    ),
    ReportingResultModel,
  );
}

export function useGetTourDetailShowReport(
  input?: getReportTourDetailShowInput,
  options?: UseQueryOptions<ApiResult<ReportTourDetailShowModel>, ApiError>,
): UseQueryResult<ApiResult<ReportTourDetailShowModel>, ApiError> {
  return QueryTransformer(
    useQuery(
      ['getTourDetailShowReports', input?.tourRefId],
      () =>
        QueryFetchFunction({
          url: `reports/tour/${input?.tourRefId}/show`,
        }),
      options,
    ),
    ReportTourDetailShowModel,
  );
}

export function useGetTourDetailSaleReport(
  input?: getReportTourDetailsInput,
  options?: UseQueryOptions<
    ExtendedApiResult<ReportTourDetailSaleModel[]>,
    ApiError
  >,
): UseQueryResult<ExtendedApiResult<ReportTourDetailSaleModel[]>, ApiError> {
  const params = input?.params;
  const tourRefId = input?.params.tourRefId;
  delete params?.tourRefId;

  return QueryTransformer(
    useQuery(
      ['getTourDetailSaleReports', input?.params],
      () =>
        QueryFetchFunction({
          url: `reports/tour/${tourRefId}/sale`,
          params,
        }),
      options,
    ),
    ReportTourDetailSaleModel,
  );
}

export function useGetTourDetailPurchaseReport(
  input?: getReportTourDetailsInput,
  options?: UseQueryOptions<
    ExtendedApiResult<ReportTourDetailPurchaseModel[]>,
    ApiError
  >,
): UseQueryResult<
  ExtendedApiResult<ReportTourDetailPurchaseModel[]>,
  ApiError
> {
  const params = input?.params;
  const tourRefId = input?.params.tourRefId;
  delete params?.tourRefId;
  return QueryTransformer(
    useQuery(
      ['getTourDetailPurchaseReports', input?.params],
      () =>
        QueryFetchFunction({
          url: `reports/tour/${tourRefId}/purchase`,
          params,
        }),
      options,
    ),
    ReportTourDetailPurchaseModel,
  );
}

export function useGetTourDetailCommissionReport(
  input?: getReportTourDetailsInput,
  options?: UseQueryOptions<
    ExtendedApiResult<ReportTourDetailCommissionModel[]>,
    ApiError
  >,
): UseQueryResult<
  ExtendedApiResult<ReportTourDetailCommissionModel[]>,
  ApiError
> {
  const params = input?.params;
  const tourRefId = input?.params.tourRefId;
  delete params?.tourRefId;
  return QueryTransformer(
    useQuery(
      ['getTourDetailCommissionReports', input?.params],
      () =>
        QueryFetchFunction({
          url: `reports/tour/${tourRefId}/commission`,
          params,
        }),
      options,
    ),
    ReportTourDetailCommissionModel,
  );
}

export function useGetTourDetailOtherExpenseReport(
  input?: getReportTourDetailsInput,
  options?: UseQueryOptions<
    ExtendedApiResult<ReportTourDetailOtherExpenseModel[]>,
    ApiError
  >,
): UseQueryResult<
  ExtendedApiResult<ReportTourDetailOtherExpenseModel[]>,
  ApiError
> {
  const params = input?.params;
  const tourRefId = input?.params.tourRefId;
  delete params?.tourRefId;
  return QueryTransformer(
    useQuery(
      ['getTourDetailOtherExpenseReport', input?.params],
      () =>
        QueryFetchFunction({
          url: `reports/tour/${tourRefId}/other-expense`,
          params,
        }),
      options,
    ),
    ReportTourDetailOtherExpenseModel,
  );
}

export function useGetTourDetailOtherIncomeReport(
  input?: getReportTourDetailsInput,
  options?: UseQueryOptions<
    ExtendedApiResult<ReportTourDetailOtherIncomeModel[]>,
    ApiError
  >,
): UseQueryResult<
  ExtendedApiResult<ReportTourDetailOtherIncomeModel[]>,
  ApiError
> {
  const params = input?.params;
  const tourRefId = input?.params.tourRefId;
  delete params?.tourRefId;
  return QueryTransformer(
    useQuery(
      ['getTourDetailOtherIncomeReport', input?.params],
      () =>
        QueryFetchFunction({
          url: `reports/tour/${tourRefId}/other-income`,
          params,
        }),
      options,
    ),
    ReportTourDetailOtherIncomeModel,
  );
}

export function useGetIncomeReport(
  input?: getReportsInput,
  options?: UseQueryOptions<ReportingResultModel, ApiError>,
): UseQueryResult<ReportingResultModel, ApiError> {
  return QueryTransformer(
    useQuery(
      ['getIncomeReport', input?.params],
      () =>
        QueryFetchFunction({
          url: `reports/income`,
          params: input?.params,
        }),
      options,
    ),
    ReportingResultModel,
  );
}

export function useGetEmployeeCommissionReport(
  input?: getReportsInput,
  options?: UseQueryOptions<ReportingResultModel, ApiError>,
): UseQueryResult<ReportingResultModel, ApiError> {
  return QueryTransformer(
    useQuery(
      ['getEmployeeCommissionReport', input?.params],
      () =>
        QueryFetchFunction({
          url: `reports/employee-commission`,
          params: input?.params,
        }),
      options,
    ),
    ReportingResultModel,
  );
}

export function useGetSalesBonusReport(
  input?: getReportsInput,
  options?: UseQueryOptions<ReportingResultModel, ApiError>,
): UseQueryResult<ReportingResultModel, ApiError> {
  return QueryTransformer(
    useQuery(
      ['getSalesBonusReport', input?.params],
      () =>
        QueryFetchFunction({
          url: `reports/employee-sale-bonus`,
          params: input?.params,
        }),
      options,
    ),
    ReportingResultModel,
  );
}

export function useGetNonSalesBonusReport(
  input?: getReportsInput,
  options?: UseQueryOptions<ReportingResultModel, ApiError>,
): UseQueryResult<ReportingResultModel, ApiError> {
  return QueryTransformer(
    useQuery(
      ['getNonSalesBonusReport', input?.params],
      () =>
        QueryFetchFunction({
          url: `reports/employee-non-sale-bonus`,
          params: input?.params,
        }),
      options,
    ),
    ReportingResultModel,
  );
}

export function useGetPassportReport(
  input?: getReportsInput,
  options?: UseQueryOptions<ReportingResultModel, ApiError>,
): UseQueryResult<ReportingResultModel, ApiError> {
  return QueryTransformer(
    useQuery(
      ['getPassportReport', input?.params],
      () =>
        QueryFetchFunction({
          url: `reports/passport-travel-document`,
          params: input?.params,
        }),
      options,
    ),
    ReportingResultModel,
  );
}

export function useGetVisaReport(
  input?: getReportsInput,
  options?: UseQueryOptions<ReportingResultModel, ApiError>,
): UseQueryResult<ReportingResultModel, ApiError> {
  return QueryTransformer(
    useQuery(
      ['getVisaReport', input?.params],
      () =>
        QueryFetchFunction({
          url: `reports/visa-travel-document`,
          params: input?.params,
        }),
      options,
    ),
    ReportingResultModel,
  );
}

export function useGetApecCardReport(
  input?: getReportsInput,
  options?: UseQueryOptions<ReportingResultModel, ApiError>,
): UseQueryResult<ReportingResultModel, ApiError> {
  return QueryTransformer(
    useQuery(
      ['getApecCardReport', input?.params],
      () =>
        QueryFetchFunction({
          url: `reports/apec-card-travel-document`,
          params: input?.params,
        }),
      options,
    ),
    ReportingResultModel,
  );
}
export function useGetInsuranceReport(
  input?: getReportsInput,
  options?: UseQueryOptions<ReportingResultModel, ApiError>,
): UseQueryResult<ReportingResultModel, ApiError> {
  return QueryTransformer(
    useQuery(
      ['getInsuranceReport', input?.params],
      () =>
        QueryFetchFunction({
          url: `reports/insurance-travel-document`,
          params: input?.params,
        }),
      options,
    ),
    ReportingResultModel,
  );
}
